import { useEffect, useMemo, useRef, useState } from "react";
import {
  eexForwardCurveClipDeliveryWindow,
  eexForwardCurveDeliveryWindow,
  eexForwardCurveNumericPrice,
  eexForwardCurveRange,
  eexForwardCurveTimeDomain,
  type EexForwardCurveDeliveryWindow,
  type EexForwardCurveTimeDomain,
} from "../../../lib/eex-forward-curve";
import type { EexMatrixDeliveryPeriod } from "../../../lib/eex-live-market";
import { cn } from "../../../lib/utils";

export type ForwardCurveChartPoint = {
  value: string | null;
  detail: string[];
};

export type ForwardCurveChartSeries = {
  id: string;
  label: string;
  kind: "live" | "snapshot" | "settlement";
  dash?: string;
  opacity?: number;
  points: ForwardCurveChartPoint[];
};

type TimelinePeriod = {
  period: EexMatrixDeliveryPeriod;
  index: number;
  fullWindow: EexForwardCurveDeliveryWindow;
  visibleWindow: EexForwardCurveDeliveryWindow;
  clippedStart: boolean;
  clippedEnd: boolean;
};

const CONTRACT_LANES: Array<{
  kind: EexMatrixDeliveryPeriod["kind"];
  shortLabel: string;
  label: string;
}> = [
  { kind: "month", shortLabel: "M", label: "Months" },
  { kind: "quarter", shortLabel: "Q", label: "Quarters" },
  { kind: "season", shortLabel: "S", label: "Seasons" },
  { kind: "year", shortLabel: "Cal", label: "Calendars" },
];

const UTC_DAY_MS = 24 * 60 * 60 * 1_000;
const AVERAGE_MONTH_DAYS = 365.2425 / 12;

export function ForwardCurveChart({
  productLabel,
  currency,
  uom,
  periods,
  series,
  displayDomain = null,
}: {
  productLabel: string;
  currency: string | null;
  uom: string | null;
  periods: EexMatrixDeliveryPeriod[];
  series: ForwardCurveChartSeries[];
  displayDomain?: EexForwardCurveTimeDomain | null;
}) {
  const [selectedPeriodKey, setSelectedPeriodKey] = useState<string | null>(periods[0]?.key ?? null);
  const [availableChartWidth, setAvailableChartWidth] = useState(0);
  const scrollRegionRef = useRef<HTMLDivElement>(null);
  const fullTimelinePeriods = useMemo(() => periods.flatMap((period, index) => {
    const window = eexForwardCurveDeliveryWindow(period);
    return window ? [{ period, index, window }] : [];
  }), [periods]);
  const naturalTimeDomain = useMemo(() => eexForwardCurveTimeDomain(periods), [periods]);
  const timeDomain = displayDomain ?? naturalTimeDomain;
  const timelinePeriods = useMemo<TimelinePeriod[]>(() => {
    if (!timeDomain) return [];
    return fullTimelinePeriods.flatMap(({ period, index, window: fullWindow }) => {
      const visibleWindow = eexForwardCurveClipDeliveryWindow(fullWindow, timeDomain);
      return visibleWindow ? [{
        period,
        index,
        fullWindow,
        visibleWindow,
        clippedStart: visibleWindow.startMs > fullWindow.startMs,
        clippedEnd: visibleWindow.endExclusiveMs < fullWindow.endExclusiveMs,
      }] : [];
    });
  }, [fullTimelinePeriods, timeDomain?.startMs, timeDomain?.endExclusiveMs]);
  const numericSeries = useMemo(() => series.map((line) => ({
    line,
    values: line.points.map((point) => eexForwardCurveNumericPrice(point.value)),
  })), [series]);
  const range = useMemo(
    () => eexForwardCurveRange(numericSeries.flatMap((line) =>
      timelinePeriods.map(({ index }) => line.values[index] ?? null)
    )),
    [numericSeries, timelinePeriods]
  );
  const canPlot = range != null && timeDomain != null;

  useEffect(() => {
    if (timelinePeriods.some(({ period }) => period.key === selectedPeriodKey)) return;
    setSelectedPeriodKey(timelinePeriods[0]?.period.key ?? periods[0]?.key ?? null);
  }, [periods, selectedPeriodKey, timelinePeriods]);

  useEffect(() => {
    if (!canPlot) return undefined;
    const element = scrollRegionRef.current;
    if (!element) return undefined;
    const updateWidth = (width: number) => {
      const next = Math.max(0, Math.floor(width));
      setAvailableChartWidth((current) => current === next ? current : next);
    };
    updateWidth(element.clientWidth);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) updateWidth(entry.contentRect.width);
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, [canPlot]);

  if (periods.length === 0) {
    return (
      <div className="rounded border border-border px-3 py-10 text-center text-xs text-muted">
        This product has no listed month, quarter, season or calendar contracts in the selected universe.
      </div>
    );
  }

  const selectedTimelinePeriod = timelinePeriods.find(({ period }) => period.key === selectedPeriodKey)
    ?? timelinePeriods[0]
    ?? null;
  const selectedIndex = selectedTimelinePeriod?.index
    ?? Math.max(0, periods.findIndex((period) => period.key === selectedPeriodKey));
  const selectedPeriod = periods[selectedIndex] ?? periods[0]!;
  const selectedPartiallyVisible = selectedTimelinePeriod != null
    && (selectedTimelinePeriod.clippedStart || selectedTimelinePeriod.clippedEnd);
  const activeLanes = CONTRACT_LANES.filter(({ kind }) =>
    timelinePeriods.some(({ period }) => period.kind === kind)
  );
  const axisUnit = [currency, uom].filter(Boolean).join("/") || "Price";
  const unplottableCount = periods.length - fullTimelinePeriods.length;
  const hasClippedPeriods = timelinePeriods.some(({ clippedStart, clippedEnd }) =>
    clippedStart || clippedEnd
  );

  const width = Math.max(chartWidth(timeDomain), availableChartWidth);
  const left = 66;
  const right = 22;
  const top = 24;
  const plotHeight = 236;
  const plotBottom = top + plotHeight;
  const tickLabelY = plotBottom + 20;
  const lanesTop = plotBottom + 34;
  const laneHeight = 22;
  const height = lanesTop + activeLanes.length * laneHeight + 16;
  const innerWidth = width - left - right;
  const xForTime = (value: number) => timeDomain
    ? left + ((value - timeDomain.startMs) / (timeDomain.endExclusiveMs - timeDomain.startMs)) * innerWidth
    : left + innerWidth / 2;
  const yForValue = (value: number) => range
    ? top + ((range.max - value) / (range.max - range.min)) * plotHeight
    : top + plotHeight / 2;
  const priceTicks = range
    ? Array.from({ length: 5 }, (_, index) => range.max - index * (range.max - range.min) / 4)
    : [];
  const timeTicks = timeDomain ? buildTimeTicks(timeDomain, innerWidth) : [];

  return (
    <div className="rounded border border-border bg-bg">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border/60 px-3 py-2">
        <div>
          <div className="text-xs font-semibold">{productLabel} forward curve</div>
          <div className="mt-0.5 text-[10px] text-muted">
            Delivery-time axis · horizontal spans show contract delivery · vertical axis {axisUnit}
          </div>
          <div className="mt-0.5 text-[9px] text-muted">
            Quarter, season and calendar prices are independent market quotes, aligned with their underlying delivery dates.
            {hasClippedPeriods ? " Edge caps mark contracts continuing outside the selected horizon." : ""}
          </div>
        </div>
        <div className="flex max-w-full flex-wrap justify-end gap-x-3 gap-y-1 text-[10px]">
          {series.map((line) => {
            const available = timelinePeriods.filter(({ index }) =>
              eexForwardCurveNumericPrice(line.points[index]?.value ?? null) != null
            ).length;
            return (
              <span key={line.id} className="inline-flex items-center gap-1.5 text-muted">
                <svg width="18" height="8" aria-hidden="true">
                  <line
                    x1="0"
                    x2="18"
                    y1="4"
                    y2="4"
                    stroke={seriesStroke(line.kind)}
                    strokeWidth={line.kind === "live" ? 3 : 2}
                    strokeDasharray={line.dash}
                    opacity={line.opacity ?? 1}
                  />
                </svg>
                <span className={line.kind === "live" ? "font-medium text-fg" : undefined}>
                  {line.label}
                </span>
                <span>{available}/{timelinePeriods.length}</span>
              </span>
            );
          })}
        </div>
      </div>

      {range && timeDomain ? (
        <div
          ref={scrollRegionRef}
          className="max-w-full overflow-x-auto"
          data-testid="eex-forward-curve-scroll-region"
        >
          <svg
            viewBox={`0 0 ${width} ${height}`}
            width={width}
            height={height}
            className="block select-none"
            role="group"
            aria-label={`${productLabel} forward prices on a continuous delivery-time axis in ${axisUnit}`}
            data-forward-curve-domain-start-ms={timeDomain.startMs}
            data-forward-curve-domain-end-exclusive-ms={timeDomain.endExclusiveMs}
          >
            <rect
              x={left}
              y={top}
              width={innerWidth}
              height={plotHeight}
              rx="4"
              fill="rgb(var(--border))"
              opacity="0.035"
            />

            {timeTicks.map((tick) => {
              const x = xForTime(tick.atMs);
              return (
                <g key={tick.atMs}>
                  <line
                    x1={x}
                    x2={x}
                    y1={top}
                    y2={plotBottom}
                    stroke="rgb(var(--border))"
                    strokeOpacity={tick.major ? 0.7 : 0.4}
                    strokeDasharray={tick.major ? undefined : "2 4"}
                  />
                  <text
                    x={x}
                    y={tickLabelY}
                    textAnchor={tick.atMs === timeDomain.startMs ? "start" : "middle"}
                    fill="rgb(var(--muted))"
                    fontSize="9"
                    fontWeight={tick.major ? 600 : 450}
                  >
                    {tick.label}
                  </text>
                </g>
              );
            })}

            {priceTicks.map((tick) => {
              const y = yForValue(tick);
              return (
                <g key={tick}>
                  <line
                    x1={left}
                    x2={width - right}
                    y1={y}
                    y2={y}
                    stroke="rgb(var(--border))"
                    strokeOpacity="0.55"
                  />
                  <text
                    x={left - 9}
                    y={y}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fill="rgb(var(--muted))"
                    fontSize="10"
                  >
                    {formatAxisPrice(tick)}
                  </text>
                </g>
              );
            })}
            <text
              x="13"
              y={top + plotHeight / 2}
              transform={`rotate(-90 13 ${top + plotHeight / 2})`}
              textAnchor="middle"
              fill="rgb(var(--muted))"
              fontSize="10"
            >
              {axisUnit}
            </text>

            {selectedTimelinePeriod && (
              <rect
                data-selected-period-key={selectedTimelinePeriod.period.key}
                x={xForTime(selectedTimelinePeriod.visibleWindow.startMs)}
                y={top}
                width={Math.max(
                  1,
                  xForTime(selectedTimelinePeriod.visibleWindow.endExclusiveMs)
                    - xForTime(selectedTimelinePeriod.visibleWindow.startMs)
                )}
                height={plotHeight}
                fill="rgb(var(--accent))"
                opacity="0.055"
                pointerEvents="none"
              />
            )}

            {numericSeries.map(({ line, values }) => (
              <g key={line.id} opacity={line.opacity ?? 1} data-series-group={line.id}>
                {timelinePeriods.map(({
                  period,
                  index,
                  fullWindow,
                  visibleWindow,
                  clippedStart,
                  clippedEnd,
                }) => {
                  const value = values[index];
                  if (value == null) return null;
                  const x1 = xForTime(visibleWindow.startMs);
                  const x2 = xForTime(visibleWindow.endExclusiveMs);
                  const y = yForValue(value);
                  return (
                    <g key={`${line.id}-${period.key}`}>
                      <line
                        data-forward-curve-span="true"
                        data-series-id={line.id}
                        data-period-key={period.key}
                        data-delivery-start={period.deliveryStart ?? undefined}
                        data-delivery-end={period.deliveryEnd ?? undefined}
                        data-visible-start-ms={visibleWindow.startMs}
                        data-visible-end-exclusive-ms={visibleWindow.endExclusiveMs}
                        data-clipped-start={clippedStart ? "true" : "false"}
                        data-clipped-end={clippedEnd ? "true" : "false"}
                        x1={x1}
                        x2={x2}
                        y1={y}
                        y2={y}
                        stroke={seriesStroke(line.kind)}
                        strokeWidth={line.kind === "live" ? 3 : 2}
                        strokeDasharray={line.dash}
                        strokeLinecap="butt"
                        onMouseEnter={() => setSelectedPeriodKey(period.key)}
                        onClick={() => setSelectedPeriodKey(period.key)}
                      >
                        <title>{`${pointTitle(period, line, index)}${clippedStart || clippedEnd ? " · partially visible in selected horizon" : ""}`}</title>
                      </line>
                      {clippedStart && (
                        <line
                          data-forward-curve-clip-cap="start"
                          data-series-id={line.id}
                          data-period-key={period.key}
                          x1={x1 + 0.5}
                          x2={x1 + 0.5}
                          y1={y - 5}
                          y2={y + 5}
                          stroke={seriesStroke(line.kind)}
                          strokeWidth={line.kind === "live" ? 3 : 2}
                          pointerEvents="none"
                        />
                      )}
                      {clippedEnd && (
                        <line
                          data-forward-curve-clip-cap="end"
                          data-series-id={line.id}
                          data-period-key={period.key}
                          x1={x2 - 0.5}
                          x2={x2 - 0.5}
                          y1={y - 5}
                          y2={y + 5}
                          stroke={seriesStroke(line.kind)}
                          strokeWidth={line.kind === "live" ? 3 : 2}
                          pointerEvents="none"
                        />
                      )}
                      {fullWindow.midpointMs >= timeDomain.startMs
                        && fullWindow.midpointMs < timeDomain.endExclusiveMs && (
                        <g
                          data-forward-curve-marker="true"
                          data-series-id={line.id}
                          data-period-key={period.key}
                          data-full-midpoint-ms={fullWindow.midpointMs}
                          data-marker-x={xForTime(fullWindow.midpointMs)}
                        >
                          <SeriesMarker
                            kind={line.kind}
                            x={xForTime(fullWindow.midpointMs)}
                            y={y}
                            stroke={seriesStroke(line.kind)}
                          />
                        </g>
                      )}
                    </g>
                  );
                })}
              </g>
            ))}

            {activeLanes.map((lane, laneIndex) => {
              const y = lanesTop + laneIndex * laneHeight;
              return (
                <g key={lane.kind} data-contract-lane={lane.kind}>
                  <text
                    x={left - 9}
                    y={y + 9}
                    textAnchor="end"
                    dominantBaseline="middle"
                    fill="rgb(var(--muted))"
                    fontSize="9"
                    fontWeight="600"
                  >
                    {lane.shortLabel}
                  </text>
                  <line
                    x1={left}
                    x2={width - right}
                    y1={y + 9}
                    y2={y + 9}
                    stroke="rgb(var(--border))"
                    strokeOpacity="0.45"
                  />
                  {timelinePeriods
                    .filter(({ period }) => period.kind === lane.kind)
                    .map(({ period, visibleWindow, clippedStart, clippedEnd }) => {
                      const x1 = xForTime(visibleWindow.startMs);
                      const x2 = xForTime(visibleWindow.endExclusiveMs);
                      const spanWidth = Math.max(1, x2 - x1);
                      const selected = period.key === selectedTimelinePeriod?.period.key;
                      const partiallyVisible = clippedStart || clippedEnd;
                      return (
                        <g
                          key={period.key}
                          role="button"
                          tabIndex={0}
                          aria-pressed={selected}
                          aria-label={`Inspect ${period.label}, ${deliveryRange(period)}${partiallyVisible ? ", partially visible in selected horizon" : ""}`}
                          onMouseEnter={() => setSelectedPeriodKey(period.key)}
                          onFocus={() => setSelectedPeriodKey(period.key)}
                          onClick={() => setSelectedPeriodKey(period.key)}
                          onKeyDown={(event) => {
                            if (event.key === "Enter" || event.key === " ") {
                              event.preventDefault();
                              setSelectedPeriodKey(period.key);
                            }
                          }}
                        >
                          <rect
                            data-contract-band="true"
                            data-period-key={period.key}
                            data-lane={lane.kind}
                            data-clipped-start={clippedStart ? "true" : "false"}
                            data-clipped-end={clippedEnd ? "true" : "false"}
                            x={x1 + 0.5}
                            y={y + 1}
                            width={Math.max(1, spanWidth - 1)}
                            height="16"
                            rx="2"
                            fill={selected ? "rgb(var(--accent))" : "rgb(var(--bg))"}
                            fillOpacity={selected ? 0.16 : 0.9}
                            stroke={selected ? "rgb(var(--accent))" : "rgb(var(--border))"}
                            strokeOpacity={selected ? 0.85 : 0.75}
                          />
                          {spanWidth >= 31 && (
                            <text
                              x={(x1 + x2) / 2}
                              y={y + 9}
                              textAnchor="middle"
                              dominantBaseline="middle"
                              fill={selected ? "rgb(var(--fg))" : "rgb(var(--muted))"}
                              fontSize="8.5"
                              fontWeight={selected ? 650 : 500}
                              pointerEvents="none"
                            >
                              {period.label}
                            </text>
                          )}
                          {clippedStart && (
                            <line
                              data-contract-band-clip-cap="start"
                              data-period-key={period.key}
                              x1={x1 + 1.5}
                              x2={x1 + 1.5}
                              y1={y + 2}
                              y2={y + 16}
                              stroke={selected ? "rgb(var(--accent))" : "rgb(var(--muted))"}
                              strokeWidth="2"
                              pointerEvents="none"
                            />
                          )}
                          {clippedEnd && (
                            <line
                              data-contract-band-clip-cap="end"
                              data-period-key={period.key}
                              x1={x2 - 1.5}
                              x2={x2 - 1.5}
                              y1={y + 2}
                              y2={y + 16}
                              stroke={selected ? "rgb(var(--accent))" : "rgb(var(--muted))"}
                              strokeWidth="2"
                              pointerEvents="none"
                            />
                          )}
                          <title>{`${lane.label} · ${period.label} · ${deliveryRange(period)}${partiallyVisible ? " · partially visible in selected horizon" : ""}`}</title>
                        </g>
                      );
                    })}
                </g>
              );
            })}
          </svg>
        </div>
      ) : (
        <div className="grid h-[300px] place-items-center px-4 text-center text-xs text-muted">
          {timeDomain
            ? "No price is available for the enabled lines and selected basis. Missing quotes remain gaps."
            : "No selected contract has a valid delivery start and end date, so no time axis can be drawn."}
        </div>
      )}

      <div className="border-t border-border/60 bg-border/[0.025] px-3 py-2">
        <div className="flex flex-wrap items-baseline justify-between gap-2">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <span>{selectedPeriod.label}</span>
            <span className="rounded border border-border/70 px-1 py-0.5 text-[8px] font-medium uppercase tracking-wide text-muted">
              {periodKindLabel(selectedPeriod.kind)}
            </span>
          </div>
          <div className="text-[10px] text-muted">
            {deliveryRange(selectedPeriod)}
            {selectedPartiallyVisible ? " · partially visible in selected horizon" : ""}
          </div>
        </div>
        <div className="mt-2 grid gap-1 sm:grid-cols-2 xl:grid-cols-4">
          {series.map((line) => {
            const point = line.points[selectedIndex];
            return (
              <div key={line.id} className="min-w-0 rounded border border-border/60 px-2 py-1.5">
                <div className="flex items-center justify-between gap-2 text-[10px] text-muted">
                  <span className="truncate">{line.label}</span>
                  <span className={cn(
                    "font-mono text-xs font-semibold",
                    point?.value == null ? "text-muted" : "text-fg"
                  )}>
                    {point?.value ?? "—"}
                  </span>
                </div>
                <div className="mt-1 break-words text-[9px] leading-4 text-muted">
                  {point?.detail.join(" · ") || "No observation for this contract"}
                </div>
              </div>
            );
          })}
        </div>
        {unplottableCount > 0 && (
          <div className="mt-2 text-[9px] text-muted">
            {unplottableCount} contract{unplottableCount === 1 ? "" : "s"} omitted from the time axis because delivery dates are unavailable or invalid.
          </div>
        )}
      </div>
    </div>
  );
}

function SeriesMarker({
  kind,
  x,
  y,
  stroke,
}: {
  kind: ForwardCurveChartSeries["kind"];
  x: number;
  y: number;
  stroke: string;
}) {
  if (kind === "settlement") {
    return (
      <rect
        x={x - 2.7}
        y={y - 2.7}
        width="5.4"
        height="5.4"
        transform={`rotate(45 ${x} ${y})`}
        fill="rgb(var(--bg))"
        stroke={stroke}
        strokeWidth="1.4"
        pointerEvents="none"
      />
    );
  }
  return (
    <circle
      cx={x}
      cy={y}
      r={kind === "live" ? 3.5 : 3}
      fill={kind === "live" ? stroke : "rgb(var(--bg))"}
      stroke={stroke}
      strokeWidth={kind === "live" ? 1.8 : 1.5}
      pointerEvents="none"
    />
  );
}

function seriesStroke(kind: ForwardCurveChartSeries["kind"]): string {
  if (kind === "live") return "rgb(var(--accent))";
  if (kind === "settlement") return "rgb(var(--warn-orange))";
  return "rgb(var(--fg))";
}

function pointTitle(
  period: EexMatrixDeliveryPeriod,
  line: ForwardCurveChartSeries,
  index: number
): string {
  const point = line.points[index];
  return [
    period.label,
    deliveryRange(period),
    line.label,
    point?.value ?? "unavailable",
    ...(point?.detail ?? []),
  ].join(" · ");
}

function periodKindLabel(kind: EexMatrixDeliveryPeriod["kind"]): string {
  if (kind === "month") return "Month";
  if (kind === "quarter") return "Quarter";
  if (kind === "season") return "Season";
  if (kind === "year") return "Calendar";
  return kind;
}

function deliveryRange(period: EexMatrixDeliveryPeriod): string {
  if (!period.deliveryStart && !period.deliveryEnd) return "Delivery dates unavailable";
  if (period.deliveryStart === period.deliveryEnd || !period.deliveryEnd) {
    return `Delivery ${period.deliveryStart}`;
  }
  return `Delivery ${period.deliveryStart ?? "?"} to ${period.deliveryEnd}`;
}

function chartWidth(domain: EexForwardCurveTimeDomain | null): number {
  if (!domain) return 860;
  const months = (domain.endExclusiveMs - domain.startMs) / UTC_DAY_MS / AVERAGE_MONTH_DAYS;
  return Math.max(860, Math.min(2_200, Math.round(132 + months * 42)));
}

function buildTimeTicks(
  domain: EexForwardCurveTimeDomain,
  innerWidth: number
): Array<{ atMs: number; label: string; major: boolean }> {
  const months = Math.max(
    1,
    (domain.endExclusiveMs - domain.startMs) / UTC_DAY_MS / AVERAGE_MONTH_DAYS
  );
  const targetTickCount = Math.max(1, Math.floor(innerWidth / 66));
  const neededInterval = Math.ceil(months / targetTickCount);
  const interval = neededInterval <= 1 ? 1 : neededInterval <= 3 ? 3 : neededInterval <= 6 ? 6 : 12;
  const firstDate = new Date(domain.startMs);
  let cursor = Date.UTC(firstDate.getUTCFullYear(), firstDate.getUTCMonth(), 1);
  if (cursor < domain.startMs) cursor = nextUtcMonth(cursor, 1);

  const result: Array<{ atMs: number; label: string; major: boolean }> = [];
  for (; cursor < domain.endExclusiveMs; cursor = nextUtcMonth(cursor, 1)) {
    const date = new Date(cursor);
    const monthIndex = date.getUTCMonth();
    const aligned = interval === 1 || monthIndex % interval === 0;
    if (!aligned) continue;
    result.push({
      atMs: cursor,
      label: timeTickLabel(date, interval, result.length === 0),
      major: monthIndex === 0 || interval >= 3,
    });
  }

  if (!result.some((tick) => tick.atMs === domain.startMs)) {
    const start = new Date(domain.startMs);
    result.unshift({
      atMs: domain.startMs,
      label: timeTickLabel(start, interval, true),
      major: true,
    });
  }
  return result;
}

function nextUtcMonth(value: number, count: number): number {
  const date = new Date(value);
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + count, 1);
}

function timeTickLabel(date: Date, interval: number, first: boolean): string {
  const monthIndex = date.getUTCMonth();
  const shortYear = String(date.getUTCFullYear()).slice(-2);
  if (interval >= 12) return String(date.getUTCFullYear());
  if (interval >= 6) return `${monthIndex < 6 ? "H1" : "H2"} '${shortYear}`;
  if (interval >= 3) return `Q${Math.floor(monthIndex / 3) + 1} '${shortYear}`;
  const month = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"][monthIndex]!;
  return monthIndex === 0 || first ? `${month} '${shortYear}` : month;
}

function formatAxisPrice(value: number): string {
  return new Intl.NumberFormat(undefined, {
    maximumFractionDigits: Math.abs(value) < 10 ? 2 : 1,
  }).format(value);
}
